import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const PRODUCT_FAMILIES = [
  'Wave Smart', 'Wave Performance', 'Wave II', 'Wave III', 'Wave XL',
  'PanelPC', 'Monitor', 'Other',
];

const KNOWN_SUPPLIERS = ['GPV', 'Ubiqconn', 'Acmepoint', 'HK Ringer'];
const SUPPLIER_LABELS = { gpv: 'GPV', ubiqconn: 'Ubiqconn', acmepoint: 'Acmepoint', hk_ringer: 'HK Ringer' };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0] || '').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

const inputStyle = {
  background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)',
  color: 'var(--ni-text)', borderRadius: 6, padding: '8px 12px',
  fontSize: 14, width: '100%', boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'var(--ni-text-muted)', textTransform: 'uppercase',
  letterSpacing: '0.05em', marginBottom: 6,
};

export default function ProcessFlowNew() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const supplierKey = ['gpv', 'ubiqconn', 'acmepoint', 'hk_ringer'].includes(role.replace(/^test-/, '')) ? role.replace(/^test-/, '') : null;

  const [docNumber, setDocNumber] = useState('');
  const [docNumberLoading, setDocNumberLoading] = useState(true);
  const [name, setName] = useState('');
  const [productFamily, setProductFamily] = useState('');
  const [suppliersSelected, setSuppliersSelected] = useState([]);
  const [description, setDescription] = useState('');
  const [gridWidth, setGridWidth] = useState(10);
  const [gridHeight, setGridHeight] = useState(10);
  const [boms, setBoms] = useState([]);
  const [bomId, setBomId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await window.fetch(`${API_BASE}/quality/next-doc-number?prefix=PF-NIN-`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.ok) setDocNumber(data.documentNumber);
      } catch { /* ignore */ }
      finally { setDocNumberLoading(false); }
    })();
  }, [getToken]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await window.fetch(`${API_BASE}/quality/boms`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        // BOMs now use the revision lifecycle — the list returns `currentStatus`
        // (Released for released + legacy docs), no longer `status: 'Active'`.
        if (data.ok) setBoms((data.boms || []).filter(b => b.currentStatus === 'Released'));
      } catch { /* ignore */ }
    })();
  }, [getToken]);

  const toggleSupplier = (s) => {
    setSuppliersSelected(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!docNumber.trim() || !name.trim()) { setError('Document number and name are required.'); return; }
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      const selectedBom = boms.find(b => b._id === bomId) || null;
      const res = await window.fetch(`${API_BASE}/quality/process-flows`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentNumber: docNumber.trim(),
          flowName: name.trim(),
          productFamily,
          suppliers: suppliersSelected,
          description,
          gridWidth: Number(gridWidth),
          gridHeight: Number(gridHeight),
          bomId: selectedBom ? selectedBom._id : '',
          bomDocumentNumber: selectedBom ? selectedBom.documentNumber : '',
          bomName: selectedBom ? selectedBom.name : '',
        }),
      });
      const data = await res.json();
      if (data.ok) {
        // If the create did not persist the BOM link, set it with a follow-up PATCH.
        if (selectedBom && data.flow.bomId !== selectedBom._id) {
          await window.fetch(`${API_BASE}/quality/process-flows/${data.flow._id}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              bomId: selectedBom._id,
              bomDocumentNumber: selectedBom.documentNumber,
              bomName: selectedBom.name,
            }),
          });
        }
        navigate(`/quality/process/${data.flow._id}`);
      } else {
        setError(data.error || 'Failed to create flow');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate('/quality/process')}
          style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 12 }}>
          ← Back to flows
        </button>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>New Process Flow</h2>
        <p style={{ margin: '6px 0 0', color: 'var(--ni-text-muted)', fontSize: 13 }}>
          Set up the basics — you can add stations and gates on the canvas after creation.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Doc number */}
          <div>
            <label style={labelStyle}>Document Number</label>
            <input value={docNumberLoading ? 'Generating…' : docNumber}
              onChange={e => setDocNumber(e.target.value)}
              disabled={docNumberLoading}
              style={{ ...inputStyle, fontFamily: 'monospace' }}
              placeholder="PF-NIN-001" />
            <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginTop: 4 }}>
              Auto-suggested — edit if needed
            </div>
          </div>

          {/* Flow name */}
          <div>
            <label style={labelStyle}>Flow Name</label>
            <input value={name} onChange={e => setName(e.target.value)}
              style={inputStyle} placeholder="e.g. Wave Smart Production Flow" required />
          </div>

          {/* Product family */}
          <div>
            <label style={labelStyle}>Product Family</label>
            <select value={productFamily} onChange={e => setProductFamily(e.target.value)} style={inputStyle}>
              <option value="">Select product family…</option>
              {PRODUCT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          {/* Attach BOM */}
          <div>
            <label style={labelStyle}>Attach BOM (optional)</label>
            <select value={bomId} onChange={e => setBomId(e.target.value)} style={inputStyle}>
              <option value="">No BOM attached</option>
              {boms.map(b => (
                <option key={b._id} value={b._id}>
                  {b.documentNumber} — {b.name}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginTop: 4 }}>
              Link a Bill of Materials to track part coverage on the canvas
            </div>
          </div>

          {/* Suppliers */}
          <div>
            <label style={labelStyle}>Assigned Suppliers</label>
            {supplierKey ? (
              <div style={{ fontSize: 13, color: 'var(--ni-text-muted)', background: 'rgba(96,165,250,0.08)',
                border: '1px solid rgba(96,165,250,0.25)', borderRadius: 8, padding: '10px 12px', lineHeight: 1.5 }}>
                🔒 Assigned to your organisation (<strong style={{ color: '#60a5fa' }}>{SUPPLIER_LABELS[supplierKey]}</strong>).
                North Invent reviews and releases the flow — this is set automatically.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {KNOWN_SUPPLIERS.map(s => {
                  const selected = suppliersSelected.includes(s);
                  return (
                    <button key={s} type="button" onClick={() => toggleSupplier(s)}
                      style={{
                        padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                        border: `1px solid ${selected ? '#60a5fa' : 'var(--ni-border-soft)'}`,
                        background: selected ? 'rgba(96,165,250,0.15)' : 'transparent',
                        color: selected ? '#60a5fa' : 'var(--ni-text-muted)',
                        cursor: 'pointer', transition: 'all 0.1s',
                      }}>
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Grid size */}
          <div>
            <label style={labelStyle}>Grid Size (columns × rows)</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input type="number" min={5} max={30} value={gridWidth}
                onChange={e => setGridWidth(e.target.value)}
                style={{ ...inputStyle, width: 80, textAlign: 'center' }} />
              <span style={{ color: 'var(--ni-text-muted)' }}>×</span>
              <input type="number" min={5} max={30} value={gridHeight}
                onChange={e => setGridHeight(e.target.value)}
                style={{ ...inputStyle, width: 80, textAlign: 'center' }} />
              <span style={{ color: 'var(--ni-text-muted)', fontSize: 12 }}>
                Each cell ≈ 1 m × 1 m
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              rows={3} style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="Brief description of the production flow…" />
          </div>

          {error && (
            <div style={{ color: '#EC2124', fontSize: 13, padding: '8px 12px',
              background: 'rgba(236,33,36,0.1)', borderRadius: 6 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={saving}
              style={{ background: '#60a5fa', color: '#000', border: 'none', borderRadius: 6,
                padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating…' : 'Create Flow'}
            </button>
            <button type="button" onClick={() => navigate('/quality/process')}
              style={{ background: 'transparent', color: 'var(--ni-text-muted)', border: '1px solid var(--ni-border-soft)',
                borderRadius: 6, padding: '10px 20px', fontSize: 14, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
